import { MultiQueryOptions, useUnscheduledSurgeriesAndMetadataQueries } from '~/hooks/tanstack-query'
import { getFlag } from '~/hooks/useFlag'
import { DepartmentKey, SelectedSpecialties } from '~/store/slices/filterSlice'
import { useStore } from '~/store/store'
import { CommonUnScheduledSurgery, UnScheduledSurgery, useCommonUnScheduledSurgeries } from '~/storm'
import { getSurgeryMetadata } from '~/utils/surgeryMetadata'

function getUnScheduledSurgeriesWhere(
    isSpecialtiesSelectionEnabled: boolean,
    departmentKey: DepartmentKey,
    selectedSpecialties: SelectedSpecialties
): Parameters<typeof useCommonUnScheduledSurgeries>[0] {
    if (isSpecialtiesSelectionEnabled) {
        if (selectedSpecialties === 'all' || selectedSpecialties.length === 0) return undefined
        return { where: { section: { speciality_uuid: { in: selectedSpecialties } } } }
    }

    if (departmentKey === 'all') return undefined
    return { where: { section: { department_id: departmentKey } } }
}

/**
 * Waiting-list surgeries selected with `surgeryMetadata`, `surgeryFolderMetadata`
 * and `section` included — exposed for consumers that call helpers like
 * `getSurgeryMetadata` which require those relations via `Include<…>` (e.g.
 * `usePractitionerPredictedWaitingList`).
 *
 * `surgeryFolderMetadata` is included so `getSurgeryMetadata` resolves to the
 * folder-level confirmation state when present (matches the legacy
 * `selectGetWaitingListForStaffing` contract).
 */
export type CurrentWaitingListUnScheduledSurgery = CommonUnScheduledSurgery

export const useBaseCurrentWaitingListForStaffing = (queryOptions?: MultiQueryOptions) => {
    const queryStatuses = useUnscheduledSurgeriesAndMetadataQueries({ staleTime: 60 * 3000, ...queryOptions })

    const isSpecialtiesSelectionEnabled = getFlag('use_specialties_selection')
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const selectedSpecialties = useStore(state => state.appFilters.selectedSpecialties)

    const unScheduledSurgeries = useCommonUnScheduledSurgeries(getUnScheduledSurgeriesWhere(isSpecialtiesSelectionEnabled, departmentKey, selectedSpecialties))

    const schedulingSurgeries = useStore(state => state.operationalPlanner.schedulingSurgeries)

    const schedulingBookingIds = new Set(schedulingSurgeries.map(s => s.bookingId))
    const optimisticUnScheduledSurgeries =
        schedulingSurgeries.length === 0 ? unScheduledSurgeries : unScheduledSurgeries.filter(surgery => !schedulingBookingIds.has(surgery.bookingId))

    const surgeriesWithMetadata: CurrentWaitingListUnScheduledSurgery[] = optimisticUnScheduledSurgeries.filter(surgery => {
        const isConfirmed = getSurgeryMetadata(surgery)?.patient_confirmed
        return !isConfirmed
    })

    // Backwards-compat alias: the same array typed as the bare
    // `UnScheduledSurgery[]`. Existing legacy consumers that pass these
    // surgeries to Denormalizer-typed helpers (`getSurgeryCategoryName`,
    // `useTransformToBarListItemBySurgeryCategory`, …) rely on this narrower
    // shape being structurally compatible with the Denormalizer `Surgery`.
    const surgeries: UnScheduledSurgery[] = surgeriesWithMetadata

    return {
        ...queryStatuses,
        isWaitingListEmpty: surgeries.length === 0,
        surgeries,
        surgeriesWithMetadata,
    }
}
